import useLocaleText from 'components/useLocaleText'
import React, { createContext, useContext, useState } from 'react'
import PromtsModal from './PromtsModal'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import { IPresetPrompt } from './PromptsTable'

interface PromptsModalsContextType {
  openPromtsModal: (opts?: IPresetPrompt & { prompt: string }) => void
  requestClosePromtsModal: () => void
}

const PromptsModalsContext = createContext<PromptsModalsContextType | null>(null)

export const usePromptsModals = () => {
  const ctx = useContext(PromptsModalsContext)
  if (!ctx) throw new Error('usePromptsModals must be used within PromptsModalsProvider')
  return ctx
}

const PromptsModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [promtsModalOpen, setPromtsModalOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  const [selectedPromt, setSelectedPromt] = useState<(IPresetPrompt & { prompt: string }) | null>(null)

  const openPromtsModal = (opts?: IPresetPrompt & { prompt: string }) => {
    setSelectedPromt(opts || null)
    setPromtsModalOpen(true)
  }

  const requestClosePromtsModal = () => {
    setConfirmationOpen(true)
  }

  const onClose = () => {
    setPromtsModalOpen(false)
    setSelectedPromt(null)
  }

  const confirmClose = () => {
    setPromtsModalOpen(false)
    setConfirmationOpen(false)
    setSelectedPromt(null)
  }

  const cancelClose = () => setConfirmationOpen(false)

  const admin_cancel_confirm = useLocaleText('admin_cancel_confirm')
  const admin_cancel_desc = useLocaleText('card_confirm_desc')
  const admin_no_cancel = useLocaleText('no')
  const admin_yes_cancel = useLocaleText('yes')

  return (
    <PromptsModalsContext.Provider value={{ openPromtsModal, requestClosePromtsModal }}>
      {children}

      {promtsModalOpen && (
        <PromtsModal isOpen prompt={selectedPromt} onRequestClose={requestClosePromtsModal} onClose={onClose} />
      )}

      {confirmationOpen && (
        <ConfirmationPopup
          isOpen
          onCancel={cancelClose}
          onConfirm={confirmClose}
          isLoading={false}
          title={admin_cancel_confirm}
          description={admin_cancel_desc}
          cancelButtonText={admin_no_cancel}
          confirmButtonText={admin_yes_cancel}
        />
      )}
    </PromptsModalsContext.Provider>
  )
}

export default PromptsModalProvider
